/* =============================================================================
   OCU Cart Drawer: Free Shipping Banner (EXCLUDING Machines + Blue Guns)
   Threshold: $150
   Exclusion method (recommended): tag products with "exclude-free-shipping-threshold"

   -----------------------------------------------------------------------------
   This file replaces the block that was previously pasted at the bottom of
   assets/app.js. It was moved out so a Pinnacle theme update can no longer
   wipe it, and three bugs were fixed:

     1. REMOVED a `document.addEventListener('click', ...)` listener that ran
        this whole routine (including a /cart.js fetch) on every tap anywhere
        on the page. Replaced with the theme's own cart events.

     2. REMOVED a MutationObserver attached to document.documentElement with
        { childList: true, subtree: true }. It only disconnected once the OCU
        drawer existed, so on any page where the drawer had not been injected
        it stayed attached to the entire DOM forever, firing on every mutation
        anywhere on the page. Replaced with a 1s presence check that costs one
        querySelector and pauses while the tab is hidden.

     3. FIXED a self-feeding loop: update() wrote textContent into a banner
        that lived inside the element the observer was watching. Each write was
        itself a childList mutation, which retriggered update(), which wrote
        again -- roughly one /cart.js request every 650ms, indefinitely. Writes
        are now skipped when the value has not changed, and the observer is
        paused around any DOM the script mutates itself.
   ============================================================================= */
(function () {
  'use strict';

  // ---------- Config (unchanged) ----------
  const THRESHOLD_CENTS = 15000; // $150.00 in cents
  const CART_ENDPOINT = '/cart.js';

  // Tag ANY product you don't want counted toward free-shipping threshold with this tag
  const EXCLUDE_TAG = 'exclude-free-shipping-threshold';

  // Fallback matching (optional): if you can't tag everything right away
  const EXCLUDED_PRODUCT_TYPES = ['Machines', 'Machine', 'Blue Guns', 'Blue Gun'];
  const EXCLUDED_HANDLE_KEYWORDS = ['machines', 'machine', 'blue-guns', 'blue_guns', 'blueguns'];

  // How often we check whether the OCU drawer has appeared. One querySelector.
  const DRAWER_POLL_MS = 1000;

  // Collapse bursts of cart fetches. Anything inside this window reuses the
  // previous response instead of hitting the network again.
  const CART_TTL_MS = 1500;

  const DEBOUNCE_MS = 650;

  function money(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  function ocuDebounce(fn, wait = 600) {
    let timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(() => fn(), wait);
    };
  }

  // ---------- Cart fetching (deduped + short TTL) ----------
  let cartPromise = null;
  let cartFetchedAt = 0;

  async function getCart() {
    const now = Date.now();

    // Reuse an in-flight request, or a very recent one. Without this, several
    // triggers landing at once each fired their own /cart.js.
    if (cartPromise && now - cartFetchedAt < CART_TTL_MS) return cartPromise;

    cartFetchedAt = now;
    cartPromise = fetch(CART_ENDPOINT, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(`cart fetch failed: ${res.status}`);
        return res.json();
      })
      .catch((err) => {
        // Don't cache a failure past this tick.
        cartPromise = null;
        cartFetchedAt = 0;
        throw err;
      });

    return cartPromise;
  }

  function invalidateCart() {
    cartPromise = null;
    cartFetchedAt = 0;
  }

  // ---------- Element lookup (unchanged) ----------
  function findDrawer() {
    return document.querySelector('.ocu-cart-drawer');
  }

  function findFooter() {
    return document.querySelector('.ocu-cart-footer') || findDrawer();
  }

  function findCheckoutButton() {
    return (
      document.querySelector('.ocu-checkout-button') ||
      document.querySelector('.ocu-cart-form button[type="submit"]') ||
      document.querySelector('button[name="checkout"]') ||
      document.querySelector('a[href*="checkout"]')
    );
  }

  function forceBannerStyles(banner) {
    if (!banner) return;

    banner.style.setProperty('color', '#ffffff', 'important');
    banner.style.setProperty('opacity', '1', 'important');
    banner.style.setProperty('filter', 'none', 'important');
    banner.style.setProperty('mix-blend-mode', 'normal', 'important');
    banner.style.setProperty('background', 'rgba(255, 255, 255, 0.14)', 'important');
    banner.style.setProperty('border', '1px solid rgba(255, 255, 255, 0.22)', 'important');
    banner.style.setProperty('padding', '10px 12px', 'important');
    banner.style.setProperty('margin', '10px 0 14px', 'important');
    banner.style.setProperty('border-radius', '8px', 'important');
    banner.style.setProperty('font-size', '13px', 'important');
    banner.style.setProperty('line-height', '1.35', 'important');

    banner.querySelectorAll('*').forEach((el) => {
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.setProperty('opacity', '1', 'important');
      el.style.setProperty('filter', 'none', 'important');
      el.style.setProperty('mix-blend-mode', 'normal', 'important');
    });

    const amount = banner.querySelector('#free-shipping-ocu-amount');
    if (amount) amount.style.setProperty('font-weight', '700', 'important');
  }

  // ---------- Scoped drawer observer, with pause/resume ----------
  // The observer watches the drawer so we react when OCU re-renders its
  // contents. Because our own banner lives inside the drawer, every DOM change
  // we make must happen with the observer paused or we retrigger ourselves.
  let drawerObserver = null;
  let watchedDrawer = null;

  function pauseObserver() {
    if (drawerObserver) drawerObserver.disconnect();
  }

  function resumeObserver() {
    if (drawerObserver && watchedDrawer && document.contains(watchedDrawer)) {
      drawerObserver.observe(watchedDrawer, { childList: true, subtree: true });
    }
  }

  // Run a DOM-mutating callback without feeding our own observer.
  function withObserverPaused(fn) {
    pauseObserver();
    try {
      return fn();
    } finally {
      resumeObserver();
    }
  }

  function ensureBanner() {
    let banner = document.getElementById('free-shipping-ocu-banner');
    if (banner) return banner;

    banner = document.createElement('div');
    banner.id = 'free-shipping-ocu-banner';
    banner.className = 'free-shipping-message';
    banner.style.display = 'none';

    banner.innerHTML = `
      <span id="free-shipping-ocu-text"></span>
      <strong><span id="free-shipping-ocu-amount"></span></strong>
    `;

    const footer = findFooter();
    const checkoutBtn = findCheckoutButton();

    if (checkoutBtn && checkoutBtn.parentNode) {
      checkoutBtn.parentNode.insertBefore(banner, checkoutBtn);
    } else if (footer) {
      footer.appendChild(banner);
    } else {
      return null;
    }

    forceBannerStyles(banner);
    return banner;
  }

  // Only touch the DOM when the value actually changed. This is the guard that
  // breaks the write -> mutation -> update -> write loop.
  function setText(el, value) {
    if (!el || el.textContent === value) return false;
    el.textContent = value;
    return true;
  }

  function setStyle(el, prop, value) {
    if (!el || el.style[prop] === value) return false;
    el.style[prop] = value;
    return true;
  }

  // ---------- Exclusion logic (unchanged) ----------
  const tagsCache = new Map(); // handle -> Set(tags)

  function getHandleFromItem(item) {
    if (item && item.handle) return String(item.handle);
    const url = item && item.url ? String(item.url) : '';
    const match = url.match(/\/products\/([^/?]+)/);
    return match ? match[1] : '';
  }

  function matchesExcludedByTypeOrHandle(item) {
    const productType = String(item.product_type || '').toLowerCase();
    const handle = String(getHandleFromItem(item) || '').toLowerCase();
    const url = String(item.url || '').toLowerCase();

    if (productType) {
      const types = EXCLUDED_PRODUCT_TYPES.map((t) => String(t).toLowerCase());
      if (types.includes(productType)) return true;
    }

    return EXCLUDED_HANDLE_KEYWORDS.some((kw) => handle.includes(kw) || url.includes(kw));
  }

  async function getTagsForHandle(handle) {
    if (!handle) return new Set();
    if (tagsCache.has(handle)) return tagsCache.get(handle);

    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) {
        const empty = new Set();
        tagsCache.set(handle, empty);
        return empty;
      }
      const data = await res.json();
      const tags = new Set(
        String(data.tags || '')
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean)
      );
      tagsCache.set(handle, tags);
      return tags;
    } catch (e) {
      const empty = new Set();
      tagsCache.set(handle, empty);
      return empty;
    }
  }

  async function isExcludedFromThreshold(item) {
    // Fast fallback checks first (doesn't require any extra requests)
    if (matchesExcludedByTypeOrHandle(item)) return true;

    // Tag-based (recommended)
    const handle = getHandleFromItem(item);
    if (!handle) return false;

    const tags = await getTagsForHandle(handle);
    return tags.has(String(EXCLUDE_TAG).toLowerCase());
  }

  async function getEligibleSubtotalCents(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return 0;

    const excludedFlags = await Promise.all(cart.items.map(isExcludedFromThreshold));

    let eligible = 0;
    cart.items.forEach((item, idx) => {
      const excluded = excludedFlags[idx];

      // Reflect discounts if present
      const line =
        typeof item.final_line_price === 'number'
          ? item.final_line_price
          : typeof item.line_price === 'number'
          ? item.line_price
          : 0;

      if (!excluded) eligible += line;
    });

    return eligible;
  }

  // ---------- Main update ----------
  let updateInFlight = false;

  async function update() {
    if (updateInFlight) return;
    if (!findDrawer()) return;

    updateInFlight = true;
    try {
      // Read + compute first, with no DOM writes, so a slow network can't
      // leave the observer paused.
      let cart;
      try {
        cart = await getCart();
      } catch (e) {
        withObserverPaused(() => {
          const b = document.getElementById('free-shipping-ocu-banner');
          setStyle(b, 'display', 'none');
        });
        return;
      }

      const isEmpty = !cart.items || cart.items.length === 0;
      let text = '';
      let amount = '';

      if (!isEmpty) {
        const eligibleTotal = await getEligibleSubtotalCents(cart);
        const remaining = THRESHOLD_CENTS - eligibleTotal;

        if (remaining > 0) {
          text = `🚚 You're `;
          amount = `${money(remaining)} away from free shipping`;
        } else {
          text = `✅ Your Order is Eligible for Free Shipping!`;
          amount = '';
        }
      }

      // The drawer can close while we were awaiting. Re-check before writing.
      if (!findDrawer()) return;

      withObserverPaused(() => {
        const banner = ensureBanner();
        if (!banner) return;

        const textEl = document.getElementById('free-shipping-ocu-text');
        const amountEl = document.getElementById('free-shipping-ocu-amount');
        if (!textEl || !amountEl) return;

        if (isEmpty) {
          setStyle(banner, 'display', 'none');
          return;
        }

        let changed = false;
        changed = setText(textEl, text) || changed;
        changed = setText(amountEl, amount) || changed;
        changed = setStyle(amountEl, 'display', amount ? 'inline' : 'none') || changed;
        changed = setStyle(banner, 'display', 'block') || changed;

        // Only re-apply the !important overrides when something actually moved.
        if (changed) forceBannerStyles(banner);
      });
    } finally {
      updateInFlight = false;
    }
  }

  const safeUpdate = ocuDebounce(update, DEBOUNCE_MS);

  // ---------- Drawer presence check ----------
  // Replaces the document-wide MutationObserver. One querySelector per second,
  // skipped entirely while the tab is in the background. Self-healing: if OCU
  // destroys and recreates its drawer, we reattach on the next tick.
  function syncDrawer() {
    if (document.hidden) return;

    const drawer = findDrawer();
    if (drawer === watchedDrawer) return;

    if (drawerObserver) {
      drawerObserver.disconnect();
      drawerObserver = null;
    }

    watchedDrawer = drawer;

    if (!drawer) return;

    drawerObserver = new MutationObserver(safeUpdate);
    drawerObserver.observe(drawer, { childList: true, subtree: true });
    safeUpdate();
  }

  setInterval(syncDrawer, DRAWER_POLL_MS);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) syncDrawer();
  });

  // ---------- Triggers ----------
  // The theme already tells us when the cart changed. This is what the old
  // global click listener was standing in for.
  ['cart:item-added', 'cart:refresh', 'cart-drawer:open'].forEach((evt) => {
    document.addEventListener(evt, () => {
      invalidateCart();
      syncDrawer();
      safeUpdate();
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncDrawer);
  } else {
    syncDrawer();
  }
})();
